import { ErrorResponse } from 'react-router-dom';
import {
  IReconciliationDashboard,
  IReconciliationDataSource,
  IReconciliationInitialValues,
  IResponse,
} from '../types';
import { prepareFormData } from '../utils/functions';
import { IUseHttpServiceProps } from '../services/type';
import { RequestMethods } from '../services/enum';

export default class ReconciliationService {
  private readonly baseUrl: string = 'reconciliation/reconciliations/';

  constructor(
    private readonly executeService: <T>(
      args: IUseHttpServiceProps,
    ) => Promise<T | ErrorResponse | null>,
    private readonly parameters: string,
  ) {}

  async fetchData(): Promise<
    IResponse<IReconciliationDataSource> | ErrorResponse | null
  > {
    return await this.executeService<IResponse<IReconciliationDataSource>>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${this.parameters}`,
    });
  }

  async fetchItemById(
    id: number,
  ): Promise<IReconciliationDataSource | ErrorResponse | null> {
    return await this.executeService<IReconciliationDataSource>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${id}/`,
    });
  }

  async deleteItemById(id: number): Promise<unknown | ErrorResponse | null> {
    return await this.executeService<unknown>({
      method: RequestMethods.DELETE,
      url: `${this.baseUrl}${id}/`,
    });
  }

  async exportData(): Promise<{ download_url: string } | ErrorResponse | null> {
    return await this.executeService<{ download_url: string }>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}export-excel/${this.parameters}`,
      contentType: 'multipart/form-data',
    });
  }

  async submitForm(
    values: IReconciliationInitialValues,
    selectedItem?: IReconciliationDataSource,
  ): Promise<IReconciliationDataSource | ErrorResponse | null> {
    const formData = prepareFormData<IReconciliationInitialValues>(values);

    const method = selectedItem ? RequestMethods.PATCH : RequestMethods.POST;
    const url = selectedItem
      ? `${this.baseUrl}${selectedItem.id}/`
      : this.baseUrl;

    return await this.executeService<IReconciliationDataSource>({
      method,
      url,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async uploadFiles(
    id: number,
    files: FileList,
  ): Promise<IReconciliationDataSource | ErrorResponse | null> {
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append('upload_files', file);
    });

    return await this.executeService<IReconciliationDataSource>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/upload-files/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async deleteFile(
    id: number,
    fileId: number,
  ): Promise<IReconciliationDataSource | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('file_id', fileId.toString());

    return await this.executeService<IReconciliationDataSource>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/delete-file/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async fetchReconciliationDashboard(
    id: number,
  ): Promise<IResponse<IReconciliationDashboard> | ErrorResponse | null> {
    return await this.executeService<IResponse<IReconciliationDashboard>>({
      method: RequestMethods.GET,
      url: `reconciliation/reconciliations/${id}/dashboard/`,
    });
  }
}
